import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { isApprover, isAdmin, hasModule, homePath, isGatePassOnly, getRoles } from "../lib/api";
import { toast } from "sonner";
import { TransferReminderPopup } from "./BranchTools";
import {
  QrCode,
  LayoutDashboard,
  BookOpen,
  HandCoins,
  Landmark,
  Users,
  Building2,
  Settings,
  LogOut,
  BellRing,
  ReceiptText,
  FileText,
  Clock,
  BarChart3,
  Boxes,
  Wallet,
  LineChart,
  ShieldCheck,
  UserCog,
  Gauge,
  Menu,
  X,
  ChevronsUpDown,
  Check,
} from "lucide-react";

const companyNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, mod: "dashboard", group: "Overview" },
  { to: "/analytics", label: "Analytics", icon: LineChart, mod: "analytics", group: "Overview" },
  { to: "/cashbook", label: "Cash Book", icon: BookOpen, mod: "cashbook", group: "Daily work" },
  { to: "/invoices", label: "Invoices", icon: FileText, mod: "invoices", group: "Daily work" },
  { to: "/store-invoices", label: "Store Invoices", icon: Boxes, mod: "storeinvoices", group: "Daily work" },
  { to: "/pending-payments", label: "Pending Payments", icon: Clock, mod: "invoices", group: "Daily work" },
  { to: "/vouchers", label: "Vouchers", icon: ReceiptText, mod: "vouchers", group: "Daily work" },
  { to: "/gatepass", label: "Gate Pass", icon: QrCode, mod: "gatepass", group: "Daily work" },
  { to: "/stock", label: "Stock", icon: Boxes, mod: "stock", group: "Daily work" },
  { to: "/credit", label: "Credit Parties", icon: HandCoins, mod: "credit", group: "Accounts & ledgers" },
  { to: "/capital-accounts", label: "Capital Withdrawings", icon: Landmark, mod: "vouchers", group: "Accounts & ledgers", acct: true },
  { to: "/salary", label: "Salary", icon: Wallet, mod: "salary", group: "Accounts & ledgers" },
  { to: "/assets", label: "Assets", icon: Boxes, mod: "assets", group: "Accounts & ledgers" },
  { to: "/reports", label: "Reports", icon: BarChart3, mod: "reports", group: "Admin & reports" },
  { to: "/settings", label: "Settings", icon: Settings, mod: "settings", group: "Admin & reports" },
];

const adminNav = [
  { to: "/admin", label: "Companies", icon: Building2, group: "Platform" },
  { to: "/admin/users", label: "Users", icon: Users, group: "Platform" },
  { to: "/admin/reports", label: "Reports", icon: LineChart, group: "Platform" },
];

const NavItem = ({ item, reminders, onClick }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === "/admin"}
      onClick={onClick}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
      className={({ isActive }) =>
        "group flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-colors border-l-2 " +
        (isActive
          ? "bg-white/10 text-white border-accent"
          : "text-primary-foreground/70 border-transparent hover:bg-white/5 hover:text-white")
      }
    >
      <Icon size={16} className="shrink-0 opacity-90" />
      {item.label}
      {item.to === "/credit" && reminders > 0 && (
        <span className="ml-auto flex items-center gap-1 text-[10px] bg-destructive text-destructive-foreground px-1.5 py-0.5 rounded-full num">
          <BellRing size={10} /> {reminders}
        </span>
      )}
      {item.badge > 0 && (
        <span className="ml-auto text-[10px] bg-accent text-accent-foreground font-bold px-1.5 py-0.5 rounded-full num">{item.badge}</span>
      )}
    </NavLink>
  );
};

export default function Layout({ children, reminders = 0 }) {
  const { user, logout, switchCompany } = useAuth();
  const navigate = useNavigate();
  const [approvalCount, setApprovalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const approver = isApprover(user);
  const admin = isAdmin(user);
  const companies = user?.companies || [];
  const multiCompany = companies.length > 1;

  const doSwitch = async (cid) => {
    if (cid === user?.active_company_id) { setSwitcherOpen(false); return; }
    setSwitching(true);
    try {
      const u = await switchCompany(cid);
      toast.success("Switched company");
      setSwitcherOpen(false);
      navigate(homePath(u || user));
    } catch (e) {
      toast.error("Could not switch company");
    } finally {
      setSwitching(false);
    }
  };

  useEffect(() => {
    if (approver && user?.platform_role !== "superadmin") {
      api.get("/deletion-requests/count").then((r) => setApprovalCount(r.data.count)).catch((e) => console.error(e));
    }
  }, [approver, user]);

  const extra = [];
  if (approver && user?.platform_role !== "superadmin")
    extra.push({ to: "/owner", label: "Owner View", icon: Gauge, group: "Admin & reports" });
  if (approver && user?.platform_role !== "superadmin")
    extra.push({ to: "/approvals", label: "Approvals", icon: ShieldCheck, badge: approvalCount, group: "Admin & reports" });
  if (admin) extra.push({ to: "/team", label: "Users", icon: UserCog, group: "Admin & reports" });
  const nav = user?.platform_role === "superadmin"
    ? adminNav
    : [...companyNav.filter((i) => hasModule(user, i.mod) && (!i.acct || getRoles(user).some((r) => r === "accountant" || r === "admin"))), ...extra];
  const groups = nav.reduce((acc, i) => { (acc[i.group] = acc[i.group] || []).push(i); return acc; }, {});
  const roleLabel =
    user?.platform_role === "superadmin"
      ? "Super Admin"
      : (user?.tenant_roles || []).join(" · ");

  return (
    <div className="min-h-screen flex bg-background">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 bg-primary text-primary-foreground flex items-center justify-between px-4 shadow-sm border-b-2 border-accent">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-7 h-7 rounded bg-accent text-accent-foreground flex items-center justify-center"><BookOpen size={15} /></span>
          <span className="font-display text-lg font-semibold">Khata</span>
          <span className="text-xs text-primary-foreground/60 truncate">{user?.company_name || "Platform Console"}</span>
        </div>
        <button data-testid="mobile-menu-btn" aria-label="Open menu" onClick={() => setOpen(true)} className="p-2 -mr-2">
          <Menu size={22} />
        </button>
      </div>

      {open && <div className="lg:hidden fixed inset-0 z-30 bg-black/50" onClick={() => setOpen(false)} />}

      <aside className={`w-64 shrink-0 bg-primary text-primary-foreground flex flex-col fixed lg:sticky top-0 h-screen z-40 transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="px-5 py-5 border-b border-white/10 flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-md bg-accent text-accent-foreground flex items-center justify-center shadow-sm"><BookOpen size={17} /></span>
              <div>
                <div className="font-display text-xl font-bold tracking-tight leading-none">Khata</div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-primary-foreground/50 mt-1">Cash book · Ledger</div>
              </div>
            </div>
            {multiCompany ? (
              <div className="relative mt-4">
                <button data-testid="company-switcher-btn" onClick={() => setSwitcherOpen((v) => !v)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-white/15 bg-white/5 hover:bg-white/10 transition-colors text-left">
                  <span className="text-xs font-semibold truncate">{user?.company_name || "Select company"}</span>
                  <ChevronsUpDown size={14} className="shrink-0 text-primary-foreground/60" />
                </button>
                {switcherOpen && (
                  <div data-testid="company-switcher-menu" className="absolute left-0 right-0 mt-1 z-50 rounded-md bg-card text-foreground shadow-lg border border-border overflow-hidden">
                    {companies.map((c) => (
                      <button key={c.id} data-testid={`switch-company-${c.id}`} disabled={switching} onClick={() => doSwitch(c.id)}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors disabled:opacity-50">
                        <span className="truncate">{c.name}</span>
                        {c.id === user?.active_company_id && <Check size={14} className="text-accent shrink-0" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-primary-foreground/70 mt-4 px-3 py-2 rounded-md border border-white/10 truncate font-semibold">
                {user?.company_name || "Platform Console"}
              </div>
            )}
          </div>
          <button data-testid="mobile-close-btn" aria-label="Close menu" onClick={() => setOpen(false)} className="lg:hidden p-1 text-primary-foreground/70 hover:text-primary-foreground">
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {Object.entries(groups).map(([g, items]) => (
            <div key={g}>
              <div className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-primary-foreground/40">{g}</div>
              <div className="space-y-0.5">
                {items.map((item) => <NavItem key={item.to} item={item} reminders={reminders} onClick={() => setOpen(false)} />)}
              </div>
            </div>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-white/10 flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center font-display font-semibold text-sm shrink-0">{(user?.name || "?").slice(0, 1).toUpperCase()}</span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-[11px] text-primary-foreground/55 capitalize truncate">{roleLabel}</div>
          </div>
          <button
            data-testid="logout-btn"
            title="Sign out"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="p-2 rounded-md text-primary-foreground/70 hover:bg-white/10 hover:text-white transition-colors"
          >
            <LogOut size={16} />
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-hidden pt-14 lg:pt-0">{children}</main>
      {!isGatePassOnly(user) && <TransferReminderPopup />}
    </div>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6 pb-4 border-b border-border animate-fade-up">
      <div className="min-w-0">
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
          {title}
        </h1>
        {subtitle && <p className="text-muted-foreground mt-0.5 text-xs lg:text-sm">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}
